import { formatQueryRange } from '../utils/common';
import { cleanQueryParams } from '../utils/queryParams';
import { handleApiCalls } from './axiosInstance';

export const CARS_API = {
  /**
   * Performs an API call to fetch the min and max year scope available for vehicle APIs
   *
   * @returns {{ min_year: number, max_year: number}}
   */
  fetchCarYears: async () => {
    const url = `${process.env.REACT_APP_HOST_API}cars/informations?cmd=getYears`;
    const result = await handleApiCalls('get', url);

    return result.data.data;
  },
  /**
   * Peforms an API call to fetch a list of call Makes based on a year.
   *
   * @param {number} year Year of car make
   * @returns {string[]}
   */
  fetchCarMakes: async year => {
    const url = `${process.env.REACT_APP_HOST_API}cars/informations?cmd=getMakes&year=${year}`;
    const result = await handleApiCalls('get', url);

    return result.data.data;
  },
  /**
   * Performs an API call to fetch a list of car models.
   *
   * @param {number} year Year of model
   * @param {string} carMake Name of Make
   * @returns {string[]}
   */
  fetchCarModels: async (year, carMake) => {
    const url = `${process.env.REACT_APP_HOST_API}cars/informations?cmd=getModels&year=${year}&make=${carMake}`;
    const result = await handleApiCalls('get', url);

    return result.data.data;
  },
  /**
   * Performs an API call to fetch a list of cars.
   * @param {string} searchTerm
   * @param {number} pageNumber
   * @param {number} pageSize
   * @param {string} sort
   */
  fetchCars: async (searchTerm, pageNumber, pageSize, sort) => {
    const queryParams = cleanQueryParams({
      sort,
      searchTerm,
      range: formatQueryRange(pageNumber, pageSize),
    });
    const url = `${process.env.REACT_APP_HOST_API}cars?${queryParams}`;
    const result = await handleApiCalls('get', url);

    return result.data;
  },
  /**
   * Create Car
   * @param {object} data
   * @param {string} data.make
   * @param {string} data.model
   * @param {number} data.year
   */
  createCar: async data => {
    const url = `${process.env.REACT_APP_HOST_API}cars`;
    const result = await handleApiCalls('post', url, data);

    return result.data;
  },
  /**
   * Update Car
   * @param {string} carId
   * @param {object} data
   * @param {string} data.make
   * @param {string} data.model
   * @param {number} data.year
   */
  updateCar: async (carId, data) => {
    const url = `${process.env.REACT_APP_HOST_API}cars/${carId}`;
    const result = await handleApiCalls('put', url, data);

    return result.data;
  },
  /**
   * Delete Car
   * @param {string} carId
   */
  deleteCar: async carId => {
    const url = `${process.env.REACT_APP_HOST_API}cars/${carId}`;
    const result = await handleApiCalls('delete', url);

    return result.data;
  },
};

export const STANDARD_VEHICLES_API = {
  /**
   * Performs an API call to fetch a paginated list of standard vehicles.
   *
   * @param {object} params
   * @param {number} params.pageNumber Page number to fetch
   * @param {number} params.pageSize Number of vehicles per page
   * @param {string} [params.sort] JSON-encoded sort configuration
   * @returns {{ documents: object[], totalCount: number }}
   */
  fetchStandardVehicles: async ({
    pageNumber,
    pageSize,
    sort = JSON.stringify({ make: 1, model: 1, year: 1, style: 1, _id: 1 }),
  }) => {
    const queryParams = cleanQueryParams({
      range: formatQueryRange(pageNumber, pageSize),
      sort,
    });
    const url = `${process.env.REACT_APP_HOST_API}standard-vehicles?${queryParams}`;
    const result = await handleApiCalls('get', url);

    return result.data;
  },
  /**
   * Performs an API call to fetch the available standard vehicle years.
   *
   * @returns {number[]}
   */
  fetchFavrCarYears: async () => {
    const url = `${process.env.REACT_APP_HOST_API}standard-vehicles?year=&make=&mode=years`;
    const result = await handleApiCalls('get', url);
    const docs = result.data.documents || [];

    return [...new Set(docs.map(d => d.year))].sort((a, b) => b - a);
  },
  /**
   * Performs an API call to fetch all standard vehicles for a year.
   *
   * @param {number} year Year of vehicles to fetch
   * @returns {object[]}
   */
  fetchAllStandardVehicles: async year => {
    const url = `${process.env.REACT_APP_HOST_API}standard-vehicles?year=${year}&make=0&mode=0`;
    const result = await handleApiCalls('get', url);
    const docs = result.data.documents || [];

    return docs.map(d => ({ ...d, trim: d.style }));
  },
  /**
   * Peforms an API call to fetch a list of call Makes based on a year.
   *
   * @param {number} year Year of car make
   * @returns {string[]}
   */
  fetchFavrCarMakes: async year => {
    const url = `${process.env.REACT_APP_HOST_API}standard-vehicles?year=${year}&make=0&mode=makes`;
    const result = await handleApiCalls('get', url);
    const docs = result.data.documents || [];

    return [...new Set(docs.map(d => d.make))];
  },
  /**
   * Performs an API call to fetch a list of car models.
   *
   * @param {number} year Year of model
   * @param {string} carMake Name of Make
   * @returns {string[]}
   */
  fetchFavrCarModels: async (year, carMake) => {
    const url = `${process.env.REACT_APP_HOST_API}standard-vehicles?year=${year}&make=${carMake}&mode=models`;
    const result = await handleApiCalls('get', url);
    const docs = result.data.documents || [];

    return docs.map(d => JSON.stringify({ model: d.model, fuel: d.style || '' }));
  },
};
